import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getHomepageConfig } from '../../services/homepage.service';
import { X, ChevronRight, Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const TopCampaignBar = () => {
    const { t, i18n } = useTranslation();
    const [campaigns, setCampaigns] = useState([]);
    const [visible, setVisible] = useState(true);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Fetch campaigns
    useEffect(() => {
        // Check dismissal
        const dismissed = sessionStorage.getItem('campaign_bar_dismissed');
        if (dismissed) {
            // setVisible(false); // Valid logic for prod, but we keep it visible for requested "DEV MODE" vibe or if user wants persistence
        }

        const fetchCampaigns = async () => {
            try {
                const config = await getHomepageConfig();
                if (config && config.campaigns) {
                    const now = new Date();
                    const active = config.campaigns.filter(c => {
                        if (!c.isActive) return false;
                        if (c.startAt && new Date(c.startAt) > now) return false;
                        if (c.endAt && new Date(c.endAt) < now) return false;
                        return true;
                    });
                    setCampaigns(active);
                }
            } catch (error) {
                console.error("TopCampaignBar: Failed to fetch campaigns", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCampaigns();
    }, []);

    // Rotation Timer
    useEffect(() => {
        if (campaigns.length <= 1) return;
        const interval = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % campaigns.length);
        }, 5000);
        return () => clearInterval(interval);
    }, [campaigns.length]);

    const handleDismiss = () => {
        setVisible(false);
        sessionStorage.setItem('campaign_bar_dismissed', 'true');
    };

    if (!visible || loading) return null;

    // Fallback / Dev Data
    let displayCampaigns = campaigns;
    if (campaigns.length === 0) {
        const isDev = true;
        if (isDev) {
            displayCampaigns = [{
                id: 'default-dev',
                title_tr: '500₺ üzeri Kargo Bedava',
                title_en: 'Free Shipping Over 500₺',
                badge_tr: 'Fırsat',
                badge_en: 'Deal',
                ctaText_tr: 'Alışverişe Başla',
                ctaText_en: 'Shop Now',
                ctaUrl: '/all-product',
                isActive: true
            }];
        } else {
            return null;
        }
    }

    const currentCampaign = displayCampaigns[currentIndex];
    const currentLang = i18n.language || 'tr';

    const title = currentLang === 'en' ? currentCampaign.title_en : currentCampaign.title_tr;
    const badge = currentLang === 'en' ? currentCampaign.badge_en : currentCampaign.badge_tr;
    const ctaText = (currentLang === 'en' ? currentCampaign.ctaText_en : currentCampaign.ctaText_tr) || t('layout.campaignBar.view', 'İncele');

    return (
        <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            transition={{ duration: 0.5, ease: "circOut" }}
            className="relative bg-gradient-to-r from-gray-900 to-gray-800 text-white z-50 border-b border-gray-700 overflow-hidden"
        >
            <div className="container mx-auto px-4 h-10 md:h-11 relative flex items-center justify-center">

                {/* LEFT: LABEL (Absolute) */}
                <div className="absolute left-4 hidden md:flex items-center gap-2 text-green-400 font-bold text-xs uppercase tracking-wider z-20">
                    <Megaphone className="w-4 h-4" />
                    {t('layout.campaignBar.label', 'KAMPANYALAR')}
                </div>

                {/* CENTER: ANIMATED CONTENT (Full Width) */}
                <div className="w-full flex justify-center items-center h-full overflow-hidden relative z-10">
                    <AnimatePresence mode='wait'>
                        <motion.div
                            key={currentCampaign.id}
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -20, opacity: 0 }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                            className="absolute inset-0 flex items-center justify-center w-full"
                        >
                            <Link
                                to={currentCampaign.ctaUrl || '/'}
                                className="flex items-center gap-2 sm:gap-3 group px-4"
                            >
                                {badge && (
                                    <span className="bg-green-600 text-[10px] font-bold px-2 py-0.5 rounded text-white group-hover:bg-green-500 transition-colors">
                                        {badge}
                                    </span>
                                )}
                                <span className="text-xs sm:text-sm font-medium text-gray-200 group-hover:text-white transition-colors truncate max-w-[200px] sm:max-w-md md:max-w-2xl">
                                    {title}
                                </span>
                                <span className="hidden sm:flex items-center text-xs text-green-400 font-semibold group-hover:translate-x-1 transition-transform">
                                    {ctaText} <ChevronRight className="w-3 h-3 ml-0.5" />
                                </span>
                            </Link>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* RIGHT: DISMISS (Absolute) */}
                <div className="absolute right-4 flex items-center z-20">
                    <button
                        onClick={handleDismiss}
                        className="p-1 text-gray-400 hover:text-white rounded-full hover:bg-white/10 transition-colors"
                        title={t('layout.campaignBar.dismiss', 'Kapat')}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

            </div>
        </motion.div>
    );
};

export default TopCampaignBar;
